import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Button, Text, ListItem as ChakraListItem, IconButton } from '@chakra-ui/react';
import { DeleteIcon, CopyIcon } from '@chakra-ui/icons';
import Locale from '../../../utils/translations';
import { Test, Template, TestStatus } from '../../../utils/interfaces';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';
import TestRunsTable from './TestRunsTable';
import TranslationsStatsTable from './TranslationsStatsTable';

interface TestItemProps {
	test: Test;
	templates: Template[];
	calculationError: { testId: string; message: string } | null;
	onRunSimulation: (testId: string, selectedSampleId?: string) => void;
	onCalculateGroundTruth: (testId: string, templateId: string, sampleId: string) => void;
	handleDuplicateTest: (test: Test) => void;
	handleDeleteTest: (testId: string) => void;
	onOpenTranslationModal: (test: Test, templateRun?: any) => void;
}

const TestItem: React.FC<TestItemProps> = ({
	test,
	templates,
	calculationError,
	onRunSimulation,
	onCalculateGroundTruth,
	handleDuplicateTest,
	handleDeleteTest,
	onOpenTranslationModal,
}) => {
	const [testStatus, setTestStatus] = useState(test.testStatus);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const s2tTestRun = test.statistics == undefined && test.type === 's2t';
	const isTranslationTestRun = !test.statistics?.isTranslationRun && test.type === 'translation';
	const selectedTemplates = useMemo(
		() =>
			Array.isArray(test.selectedTemplateId)
				? templates.filter(t => test.selectedTemplateId.includes(t.templateId))
				: [],
		[test.selectedTemplateId, templates],
	);

	const fetchStatus = useCallback(async (testId: string) => {
		const api = new BonfireAPI(ApiType.QaApi);
		const url = `${config.qa_api.url}/aiModelComparisonsTests/updateStatus`;
		try {
			const res = await api.post(url, { testId });
			if (res?.testStatus) {
				setTestStatus(res.testStatus);
			}
		} catch {
			console.error('Failed to fetch test status');
		}
	}, []);

	const runGroundTruth = useCallback(async () => {
		for (const tid of test.selectedTemplateId) {
			await onCalculateGroundTruth(test.testId, tid, test.selectedSampleId);
		}
	}, [onCalculateGroundTruth, test.selectedSampleId, test.selectedTemplateId, test.testId]);

	const formatLanguageKey = (key: string): string => {
		if (!key) {
			return '';
		}
		const parts = key.split('_');
		if (parts.length !== 2) {
			return key.toUpperCase();
		}
		return `${parts[0].toUpperCase()} - ${parts[1].toUpperCase()}`;
	};

	useEffect(() => {
		console.log('Test status changed:', testStatus);
		if (testStatus == TestStatus.COMPLETED && (s2tTestRun || isTranslationTestRun)) {
			runGroundTruth();
		}
	}, [testStatus]);

	useEffect(() => {
		if (testStatus === TestStatus.RUNNING) {
			fetchStatus(test.testId);
			pollingRef.current = setInterval(() => fetchStatus(test.testId), 5000);
		}
		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current);
				pollingRef.current = null;
			}
		};
	}, [fetchStatus, testStatus, test.testId]);

	const canModify = testStatus === TestStatus.COMPLETED || testStatus === TestStatus.PENDING;

	return (
		<ChakraListItem p={4} bg="white" borderRadius="md" boxShadow="sm">
			<Flex align="center" justify="space-between" mb={2}>
				<Flex fontSize="md" fontWeight="semibold" color="gray.600" direction="column">
					<Flex gap={1} align="center">
						Status:{' '}
						<Text
							as="span"
							color={testStatus === 'running' ? 'orange.500' : 'green.600'}
						>
							{testStatus}
						</Text>
					</Flex>
					<Text>Test type: {test.type ?? 's2t'}</Text>
				</Flex>
				<Flex gap={2}>
					<IconButton
						size="sm"
						variant="beDanger"
						aria-label="Delete Test"
						icon={<DeleteIcon />}
						isDisabled={!canModify}
						onClick={() => handleDeleteTest(test.testId)}
					/>
					<IconButton
						size="sm"
						variant="bePrimary"
						aria-label="Duplicate / Translate Test"
						icon={<CopyIcon />}
						onClick={() => {
							if (test.type === 'translation') {
								onOpenTranslationModal(test);
							} else {
								handleDuplicateTest(test);
							}
						}}
					/>
				</Flex>
			</Flex>

			<Flex direction="column" gap={2}>
				<Flex align="center" justify="space-between" my={2}>
					<Text fontWeight="bold" fontSize="xl">
						Test:{' '}
						<Box as="span" fontWeight={500}>
							{test.name}
						</Box>
					</Text>
					<Button
						size="md"
						variant="outline"
						onClick={async () => {
							await onRunSimulation(test.testId, test.selectedSampleId);
							setTestStatus(TestStatus.RUNNING);
						}}
						isDisabled={!!test.statistics || testStatus !== TestStatus.PENDING}
					>
						{Locale.get('Run simulation')}
					</Button>
				</Flex>

				{selectedTemplates.length > 0 && (
					<Flex align="flex-start" justify="space-between" mb={1}>
						<Box>
							<Text fontWeight="semibold" fontSize="lg" mb={1}>
								Selected Templates:
							</Text>
							{selectedTemplates.map(t => (
								<Text key={t.templateId} fontSize="md" ml={2}>
									{t.name}
								</Text>
							))}
						</Box>
						<Box>
							<Text color="red.500" fontSize="13px" fontWeight="bold">
								{calculationError?.testId === test.testId &&
									calculationError?.message}
							</Text>
						</Box>
					</Flex>
				)}

				{test.type === 's2t' && !!test.historyOfRuns?.length && (
					<TestRunsTable
						historyOfRuns={test.historyOfRuns}
						onCalculateTranslationOpen={(sampleId, template) => {
							onOpenTranslationModal(
								{
									...test,
									selectedSampleId: sampleId,
									selectedTemplateId: [template.templateId],
								},
								template,
							);
						}}
					/>
				)}
				{test.type === 'translation' &&
					!!test.translationResults?.length &&
					test.translationResults.map((templateResult: any, idx: number) => (
						<Box key={templateResult.templateId || idx}>
							<TranslationsStatsTable
								templateResult={templateResult}
								formatLanguageKey={formatLanguageKey}
							/>{' '}
						</Box>
					))}
			</Flex>
		</ChakraListItem>
	);
};

export default React.memo(TestItem);
